/**
 * Turning a cell into a place name, in the browser (brief §10).
 *
 * Reverse geocoding happens **in the client**, against Nominatim, cached per cell, and is
 * allowed to fail — then whatever wanted the name does without it. The server does no
 * geocoding because it has no coordinate to geocode, and that is the privacy property from
 * brief §2 rather than a limitation to work around.
 *
 * It lives in its own module because two very different things want it: the geofence alert
 * ("Erhöhte Dichte in der Königstraße") and the share card. One implementation and one cache,
 * so a place looked up for an alert is free when the same cell is shared.
 */

import { centreOf } from '$domain/h3.ts';

const CACHE_KEY = 'hotspotter.places';

function readCache(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) ?? '{}') as Record<string, string>;
  } catch {
    return {};
  }
}

/**
 * A name as it is shown. Nominatim names a nameless spot `""` rather than leaving it out, and
 * the labels cached before that was caught began with a comma — ", Stuttgart-Mitte".
 */
function tidy(label: string | undefined): string | undefined {
  const tidied = label?.replace(/^(\s*,\s*)+/, '').trim();
  return tidied === '' ? undefined : tidied;
}

/**
 * A name already looked up, without a request. For callers that have several cells to name
 * and must space their real lookups out: Nominatim allows one request a second, and a cached
 * name should not have to wait in that queue.
 */
export function cachedPlace(cell: string): string | undefined {
  return tidy(readCache()[cell]);
}

export async function reverseGeocode(cell: string): Promise<string | undefined> {
  const cache = readCache();
  const known = tidy(cache[cell]);
  if (known !== undefined) return known;

  try {
    const centre = centreOf(cell);
    const url = new URL('https://nominatim.openstreetmap.org/reverse');
    url.searchParams.set('format', 'jsonv2');
    // Five decimals is about a metre, which is far finer than the cell this came from — the
    // centre of a res-9 hexagon is not anybody's position, and Nominatim is asked at zoom 16
    // so the answer is a street or a quarter rather than a house number.
    url.searchParams.set('lat', centre.lat.toFixed(5));
    url.searchParams.set('lon', centre.lon.toFixed(5));
    url.searchParams.set('zoom', '16');

    const response = await fetch(url, { headers: { accept: 'application/json' } });
    if (!response.ok) return undefined;
    const body = (await response.json()) as {
      name?: string;
      address?: { suburb?: string; city?: string; town?: string; village?: string };
    };
    const area = body.address?.suburb;
    const place = body.address?.city ?? body.address?.town ?? body.address?.village;
    const label = [body.name, area, place]
      .filter((part) => part !== undefined && part.trim() !== '')
      .join(', ');
    if (label === '') return undefined;

    cache[cell] = label;
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    return label;
  } catch {
    return undefined;
  }
}
